import { DEFAULT_BINDINGS } from '../core/constants';
import type { InputAction, InputBindings, InputState } from '../core/types';

export interface KeyboardInputEvent {
  code?: string;
  key?: string;
  repeat?: boolean;
  target?: unknown;
  preventDefault?: () => void;
}

export interface RebindOptions {
  /** Remove the assigned codes from every other action. Defaults to true. */
  exclusive?: boolean;
}

const INPUT_ACTIONS: InputAction[] = [
  'accelerate',
  'brake',
  'left',
  'right',
  'handbrake',
  'restart',
  'reset',
  'pause',
];

function cloneBindings(bindings: InputBindings): InputBindings {
  return {
    accelerate: [...bindings.accelerate],
    brake: [...bindings.brake],
    left: [...bindings.left],
    right: [...bindings.right],
    handbrake: [...bindings.handbrake],
    restart: [...bindings.restart],
    reset: [...bindings.reset],
    pause: [...bindings.pause],
  };
}

/** Converts common KeyboardEvent.key values into stable KeyboardEvent.code values. */
export function normalizeInputCode(value: string): string {
  if (value === ' ') {
    return 'Space';
  }
  const code = value.trim();
  if (code === '') {
    return '';
  }

  if (/^Key[A-Z]$/i.test(code)) {
    return `Key${code.at(-1)!.toUpperCase()}`;
  }
  if (/^Digit[0-9]$/i.test(code)) {
    return `Digit${code.at(-1)}`;
  }
  if (/^[a-z]$/i.test(code)) {
    return `Key${code.toUpperCase()}`;
  }
  if (/^[0-9]$/.test(code)) {
    return `Digit${code}`;
  }

  const aliases: Record<string, string> = {
    space: 'Space',
    spacebar: 'Space',
    esc: 'Escape',
    escape: 'Escape',
    backspace: 'Backspace',
    arrowup: 'ArrowUp',
    arrowdown: 'ArrowDown',
    arrowleft: 'ArrowLeft',
    arrowright: 'ArrowRight',
  };

  return aliases[code.toLowerCase()] ?? code;
}

function normalizeCodeList(codes: readonly string[]): string[] {
  const unique = new Set<string>();
  for (const rawCode of codes) {
    const code = normalizeInputCode(rawCode);
    if (code !== '') {
      unique.add(code);
    }
  }
  return [...unique];
}

/** Human-readable label for a normalized KeyboardEvent.code value. */
export function formatInputCode(value: string): string {
  const code = normalizeInputCode(value);
  if (code === '') {
    return 'Unbound';
  }
  if (/^Key[A-Z]$/.test(code) || /^Digit[0-9]$/.test(code)) {
    return code.at(-1)!;
  }

  const labels: Record<string, string> = {
    Space: 'Spacebar',
    Escape: 'Esc',
    Backspace: 'Backspace',
    ArrowUp: 'Arrow Up',
    ArrowDown: 'Arrow Down',
    ArrowLeft: 'Arrow Left',
    ArrowRight: 'Arrow Right',
  };

  return labels[code] ?? code;
}

function isEditableElement(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as {
    tagName?: unknown;
    isContentEditable?: unknown;
    getAttribute?: (name: string) => string | null;
    closest?: (selector: string) => unknown;
  };
  const tagName = typeof candidate.tagName === 'string' ? candidate.tagName.toLowerCase() : '';

  if (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    candidate.isContentEditable === true ||
    candidate.getAttribute?.('role') === 'textbox'
  ) {
    return true;
  }

  try {
    return Boolean(
      candidate.closest?.(
        'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"]',
      ),
    );
  } catch {
    return false;
  }
}

function isNativeActivationTarget(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as {
    tagName?: unknown;
    getAttribute?: (name: string) => string | null;
    closest?: (selector: string) => unknown;
  };
  const tagName = typeof candidate.tagName === 'string' ? candidate.tagName.toLowerCase() : '';
  if (tagName === 'button' || tagName === 'a' || tagName === 'summary') {
    return true;
  }

  const role = candidate.getAttribute?.('role');
  if (role === 'button' || role === 'menuitem' || role === 'switch' || role === 'tab') {
    return true;
  }

  try {
    return Boolean(
      candidate.closest?.(
        'button, a, summary, [role="button"], [role="menuitem"], [role="switch"], [role="tab"]',
      ),
    );
  } catch {
    return false;
  }
}

function eventCode(event: KeyboardInputEvent): string {
  return normalizeInputCode(event.code || event.key || '');
}

/**
 * Central keyboard action mapper. Action edges are generated only when an
 * action changes from up to down, even if multiple keys are bound to it.
 */
export class InputManager {
  private bindings: InputBindings;
  private readonly heldCodes = new Set<string>();
  private readonly pressedActions = new Set<InputAction>();
  private readonly releasedActions = new Set<InputAction>();
  private attachedTarget: EventTarget | null = null;
  private enabled = true;

  private readonly onKeyDown = (event: Event) => {
    this.handleKeyDown(event as KeyboardEvent);
  };

  private readonly onKeyUp = (event: Event) => {
    this.handleKeyUp(event as KeyboardEvent);
  };

  private readonly onBlur = () => {
    this.clear();
  };

  constructor(bindings: InputBindings = DEFAULT_BINDINGS, target?: EventTarget | null) {
    this.bindings = cloneBindings(DEFAULT_BINDINGS);
    this.setBindings(bindings);
    if (target) {
      this.attach(target);
    }
  }

  attach(target: EventTarget | null = typeof window === 'undefined' ? null : window): boolean {
    if (!target) {
      return false;
    }
    if (this.attachedTarget === target) {
      return true;
    }

    this.detach();
    this.attachedTarget = target;
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
    return true;
  }

  detach(): void {
    if (!this.attachedTarget) {
      return;
    }

    this.attachedTarget.removeEventListener('keydown', this.onKeyDown);
    this.attachedTarget.removeEventListener('keyup', this.onKeyUp);
    this.attachedTarget.removeEventListener('blur', this.onBlur);
    this.attachedTarget = null;
    this.clear();
  }

  dispose(): void {
    this.detach();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clear();
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getBindings(): InputBindings {
    return cloneBindings(this.bindings);
  }

  getCodesForAction(action: InputAction): string[] {
    return [...this.bindings[action]];
  }

  setBindings(bindings: Partial<InputBindings>): InputBindings {
    const next = cloneBindings(this.bindings);
    for (const action of INPUT_ACTIONS) {
      const codes = bindings[action];
      if (codes) {
        const normalized = normalizeCodeList(codes);
        // Old or hand-edited saves can contain an empty action. Keep the
        // current working binding (or its default) instead of making that
        // action impossible to recover from inside the settings screen.
        next[action] =
          normalized.length > 0
            ? normalized
            : next[action].length > 0
              ? next[action]
              : [...DEFAULT_BINDINGS[action]];
      }
    }

    for (const action of INPUT_ACTIONS) {
      if (next[action].length === 0) {
        next[action] = [...DEFAULT_BINDINGS[action]];
      }
    }

    this.bindings = next;
    this.clear();
    return this.getBindings();
  }

  resetBindings(): InputBindings {
    this.bindings = cloneBindings(DEFAULT_BINDINGS);
    this.clear();
    return this.getBindings();
  }

  rebind(
    action: InputAction,
    codeOrCodes: string | readonly string[],
    options: RebindOptions = {},
  ): InputBindings {
    const codes = normalizeCodeList(
      typeof codeOrCodes === 'string' ? [codeOrCodes] : codeOrCodes,
    );
    const exclusive = options.exclusive ?? true;
    const next = this.getBindings();

    // Treat an empty key capture as a cancelled rebind. An action should never
    // disappear merely because a browser supplied an unusable key value.
    if (codes.length === 0) {
      return next;
    }

    if (exclusive) {
      const assigned = new Set(codes);
      const releasedCodes = next[action].filter((code) => !assigned.has(code));
      const displacedActions: Array<{ action: InputAction; removedCodes: string[] }> = [];

      for (const otherAction of INPUT_ACTIONS) {
        if (otherAction !== action) {
          const previousCodes = next[otherAction];
          const remainingCodes = previousCodes.filter((code) => !assigned.has(code));
          const removedCodes = previousCodes.filter((code) => assigned.has(code));
          next[otherAction] = remainingCodes;
          if (remainingCodes.length === 0 && removedCodes.length > 0) {
            displacedActions.push({ action: otherAction, removedCodes });
          }
        }
      }

      next[action] = codes;

      const occupied = new Set(INPUT_ACTIONS.flatMap((inputAction) => next[inputAction]));
      for (const displaced of displacedActions) {
        const swapCode = releasedCodes.find((code) => !occupied.has(code));
        const defaultCode = DEFAULT_BINDINGS[displaced.action].find(
          (code) => !occupied.has(code),
        );
        // A single-key collision performs the intuitive swap (for example,
        // binding Pause to R moves Restart to Escape). For unusual multi-key
        // collisions, sharing the displaced code is preferable to leaving an
        // action empty and unrecoverable.
        const replacement = swapCode ?? defaultCode ?? displaced.removedCodes[0];
        next[displaced.action] = [replacement];
        occupied.add(replacement);
      }
    } else {
      next[action] = codes;
    }

    this.bindings = next;
    this.clear();
    return this.getBindings();
  }

  bind(action: InputAction, codeOrCodes: string | readonly string[]): InputBindings {
    return this.rebind(action, codeOrCodes);
  }

  handleKeyDown(event: KeyboardInputEvent): boolean {
    if (!this.enabled || isEditableElement(event.target)) {
      return false;
    }

    const code = eventCode(event);
    // Preserve the browser's native Space/Enter activation semantics while a
    // menu control is focused. Space remains the handbrake everywhere else.
    if ((code === 'Space' || code === 'Enter') && isNativeActivationTarget(event.target)) {
      return false;
    }
    const actions = this.actionsForCode(code);
    if (code === '' || actions.length === 0) {
      return false;
    }

    const heldBefore = actions.map((action) => this.isHeld(action));
    this.heldCodes.add(code);
    actions.forEach((action, index) => {
      if (!heldBefore[index] && this.isHeld(action)) {
        this.pressedActions.add(action);
      }
    });
    event.preventDefault?.();
    return true;
  }

  handleKeyUp(event: KeyboardInputEvent): boolean {
    const code = eventCode(event);
    if ((code === 'Space' || code === 'Enter') && isNativeActivationTarget(event.target)) {
      return false;
    }
    const actions = this.actionsForCode(code);
    if (code === '' || actions.length === 0) {
      return false;
    }

    const heldBefore = actions.map((action) => this.isHeld(action));
    this.heldCodes.delete(code);
    actions.forEach((action, index) => {
      if (heldBefore[index] && !this.isHeld(action)) {
        this.releasedActions.add(action);
      }
    });
    if (!isEditableElement(event.target)) {
      event.preventDefault?.();
    }
    return true;
  }

  /** Test- and controller-friendly alternative to constructing DOM events. */
  pressCode(code: string): boolean {
    return this.handleKeyDown({ code });
  }

  /** Test- and controller-friendly alternative to constructing DOM events. */
  releaseCode(code: string): boolean {
    return this.handleKeyUp({ code });
  }

  isHeld(action: InputAction): boolean {
    return this.bindings[action].some((code) => this.heldCodes.has(code));
  }

  wasPressed(action: InputAction): boolean {
    return this.pressedActions.has(action);
  }

  wasReleased(action: InputAction): boolean {
    return this.releasedActions.has(action);
  }

  consumePress(action: InputAction): boolean {
    const pressed = this.pressedActions.has(action);
    this.pressedActions.delete(action);
    return pressed;
  }

  consumeRelease(action: InputAction): boolean {
    const released = this.releasedActions.has(action);
    this.releasedActions.delete(action);
    return released;
  }

  peekState(): InputState {
    return {
      throttle: this.isHeld('accelerate') ? 1 : 0,
      brake: this.isHeld('brake') ? 1 : 0,
      steer: Number(this.isHeld('right')) - Number(this.isHeld('left')),
      handbrake: this.isHeld('handbrake'),
      restartPressed: this.wasPressed('restart'),
      resetPressed: this.wasPressed('reset'),
      pausePressed: this.wasPressed('pause'),
    };
  }

  /** Returns one frame of input and consumes every edge-triggered action. */
  consumeState(): InputState {
    const state = this.peekState();
    this.endFrame();
    return state;
  }

  getState(): InputState {
    return this.consumeState();
  }

  endFrame(): void {
    this.pressedActions.clear();
    this.releasedActions.clear();
  }

  clear(): void {
    this.heldCodes.clear();
    this.endFrame();
  }

  private actionsForCode(code: string): InputAction[] {
    if (code === '') {
      return [];
    }
    return INPUT_ACTIONS.filter((action) => this.bindings[action].includes(code));
  }
}

export function createInputManager(
  bindings: InputBindings = DEFAULT_BINDINGS,
  target?: EventTarget | null,
): InputManager {
  return new InputManager(bindings, target);
}

export const inputManager = new InputManager();
